// Global Instructions Rule Applied!

import React, { useState, useEffect, useCallback, forwardRef, useRef } from 'react'
import Select from '../../Select.jsx'
import { useCitySearch } from '../../../hooks_deprecated/useCitySearch.js'
import { InputSize, FieldValue } from '../types.ts'
import { SelectListEntry } from '../../../../state_manager/data_models/select_list_entry.ts'

export interface CitySelectProps {
  value: FieldValue
  onChange?: (value: string, option?: SelectListEntry | null) => void
  onSelect?: (value: string, option?: SelectListEntry) => void
  onClear?: () => void
  mode?: 'form' | 'inline' | 'cell'
  contextKey?: string
  country?: string | null
  required?: boolean
  placeholder?: string
  size?: InputSize
  error?: boolean
  style?: React.CSSProperties
  className?: string
  autoFocus?: boolean
  onKeyDown?: (e: React.KeyboardEvent) => void
  disabled?: boolean
}

export const CitySelect = forwardRef<any, CitySelectProps>(
  (
    {
      value,
      onChange,
      onSelect,
      onClear,
      mode = 'form',
      contextKey,
      country = null,
      required = false,
      placeholder = 'Search cities...',
      size = 'small',
      error = false,
      style = {},
      className = '',
      autoFocus,
      onKeyDown,
      disabled,
      ...selectProps
    },
    ref
  ) => {
    const {
      searchCities,
      loading: citySearchLoading,
      results: citySearchResults,
      clearResults: clearCityResults,
      hasSearched: cityHasSearched
    } = useCitySearch()

    const [selectedCity, setSelectedCity] = useState<SelectListEntry | null>(null)
    const [hasBeenCleared, setHasBeenCleared] = useState(false)
    const selectedCityRef = useRef<SelectListEntry | null>(null)
    const previousCountryRef = useRef<string | null>(country)
    const previousValueRef = useRef<any>(value)

    useEffect(() => {
      selectedCityRef.current = selectedCity
    }, [selectedCity])

    // Clear city when country changes, unless city is being selected (value changing)
    useEffect(() => {
      const previousCountry = previousCountryRef.current
      const previousValue = previousValueRef.current

      // Skip if country hasn't changed
      if (previousCountry === country) {
        previousValueRef.current = value
        return
      }

      // Update refs
      previousCountryRef.current = country
      previousValueRef.current = value

      // Clear search results when country changes
      clearCityResults()

      // If value is changing (city selection), preserve city - country change is from city selection
      // If value is NOT changing (manual country change), clear city
      const valueIsChanging = previousValue !== value

      if (valueIsChanging) {
        // City is being selected - preserve it even though country is changing
        return
      }

      // Country changed manually - clear city
      setSelectedCity(null)
      setHasBeenCleared(true)
    }, [country, clearCityResults, value])

    // Handle value prop changes - restore city from value
    useEffect(() => {
      // Handle empty value
      if (!value || value === null || value === undefined) {
        if (selectedCityRef.current) {
          setSelectedCity(null)
        }
        if (hasBeenCleared) {
          setHasBeenCleared(false)
        }
        return
      }

      // Handle object value (city object with full data)
      if (typeof value === 'object' && value !== null) {
        const cityObject = value as any
        const cityCountry = cityObject?.data?.countryCode

        // If city was cleared due to country change, check if city's country matches current country
        if (hasBeenCleared) {
          // Only restore if city's country matches the current country
          if (cityCountry && cityCountry === country) {
            const selectListEntry: SelectListEntry = {
              label: cityObject.label || cityObject.name || '',
              value: cityObject.value,
              name: cityObject.name || cityObject.label || '',
              data: cityObject.data || {}
            }
            setSelectedCity(selectListEntry)
            setHasBeenCleared(false)
          }
          // If country doesn't match, don't restore (keep cleared)
          return
        }

        // Normal case: restore city from value
        // But only if city's country matches current country (or no country filter is set)
        if (country && cityCountry && cityCountry !== country) {
          // City's country doesn't match current country - don't restore
          if (selectedCityRef.current) {
            setSelectedCity(null)
          }
          return
        }

        const selectListEntry: SelectListEntry = {
          label: cityObject.label || cityObject.name || '',
          value: cityObject.value,
          name: cityObject.name || cityObject.label || '',
          data: cityObject.data || {}
        }
        const currentSelected = selectedCityRef.current
        // Only update if value actually changed
        if (!currentSelected || currentSelected.value !== selectListEntry.value) {
          setSelectedCity(selectListEntry)
        }
        return
      }

      // Handle string/number value (city code)
      const cityValue = typeof value === 'string' || typeof value === 'number' ? value : null
      if (cityValue === null) {
        return
      }

      const currentSelected = selectedCityRef.current
      const valueStr = String(cityValue)
      const valueNum = typeof cityValue === 'number' ? cityValue : parseInt(valueStr)

      // Skip if already selected
      if (
        currentSelected &&
        (currentSelected.value === cityValue || currentSelected.value === valueNum || currentSelected.name === valueStr)
      ) {
        return
      }

      // Try to find city in search results
      const existingCity = citySearchResults.find(
        (city: any) =>
          city.value === cityValue || city.value === valueNum || city.name === valueStr || city.label === valueStr
      )

      if (existingCity) {
        const selectListEntry: SelectListEntry = {
          label: existingCity.label,
          value: existingCity.value,
          name: existingCity.name
        }
        setSelectedCity(selectListEntry)
      } else {
        // Create temporary entry if not found in results
        const isNumericId = !isNaN(valueNum) && !isNaN(parseInt(valueStr))
        const cityLabel = isNumericId ? `City ID: ${valueStr}` : valueStr
        const tempCity: SelectListEntry = {
          label: cityLabel,
          value: isNumericId ? valueNum : valueStr,
          name: cityLabel
        }
        setSelectedCity(tempCity)
      }
    }, [value, hasBeenCleared, citySearchResults])

    useEffect(() => {
      const currentSelected = selectedCityRef.current
      if (!currentSelected || citySearchResults.length === 0) {
        return
      }

      const matchingCity = citySearchResults.find(
        (city: any) => city.value === currentSelected.value || city.name === currentSelected.name
      )

      if (
        matchingCity &&
        (matchingCity.label !== currentSelected.label ||
          matchingCity.value !== currentSelected.value ||
          matchingCity.name !== currentSelected.name)
      ) {
        const selectListEntry: SelectListEntry = {
          label: matchingCity.label,
          value: matchingCity.value,
          name: matchingCity.name
        }
        setSelectedCity(selectListEntry)
      }
    }, [citySearchResults])

    const handleCitySelect = useCallback(
      (selectedValue: any, option: any) => {
        if (option) {
          setSelectedCity(option)
          onChange?.(selectedValue, option)
          onSelect?.(selectedValue, option)
        }
      },
      [onChange, onSelect]
    )

    const handleClear = useCallback(() => {
      setSelectedCity(null)
      setHasBeenCleared(true)
      clearCityResults()
      onChange?.('', null)
      onClear?.()
    }, [onChange, onClear, clearCityResults])

    const handleSearch = useCallback(
      (searchValue: string) => {
        if (searchValue && searchValue.length >= 2) {
          searchCities(searchValue, country)
        } else if (!searchValue) {
          clearCityResults()
        }
      },
      [searchCities, clearCityResults, country]
    )

    const formatOption = (city: any): SelectListEntry & { data: any } => ({
      label: city.label,
      value: city.value,
      name: city.name,
      data: city
    })

    const options: SelectListEntry[] = selectedCity
      ? [
          formatOption(selectedCity),
          ...citySearchResults.filter((result: any) => result.name !== selectedCity.name).map(formatOption)
        ]
      : cityHasSearched
        ? citySearchResults.map(formatOption)
        : []

    const placeholderText = required ? `${placeholder}` : placeholder

    return (
      <Select
        ref={ref}
        value={selectedCity ? selectedCity.value : undefined}
        onChange={handleCitySelect}
        onClear={handleClear}
        onSearch={handleSearch}
        loading={citySearchLoading}
        options={options}
        placeholder={placeholderText}
        size={size}
        style={{
          width: '100%',
          ...style,
          borderColor: style.borderColor || (error ? '#ff4d4f' : undefined)
        }}
        className={className}
        status={error ? 'error' : undefined}
        filterOption={false}
        showSearch={true}
        optionLabelProp='label'
        notFoundContent={
          citySearchLoading ? 'Searching...' : cityHasSearched ? 'No cities found' : 'Type to start searching'
        }
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        disabled={disabled}
        {...selectProps}
      />
    )
  }
)

CitySelect.displayName = 'CitySelect'
